<template>
  <modal-fullscreen
    title="Attached Document"
    width="600px"
    :visible="visible"
    :destroy-on-close="true"
    @cancel="closeModal"
    v-if="visible"
  >
    <div class="document-actions" v-if="document">
      <div class="flex flex-column gap-4">
        <h4 class="m-0">Actions</h4>
        <export-button v-if="document" :export-payload="exportPayload" :button-style="{ width: '100%' }" />
        <a-button icon="eye" @click="handleAttachedView" v-if="document.parent_id">View Previous Version</a-button>
      </div>
    </div>

    <template>
      <div v-if="loading || !document" class="loading-spin" style="height: 650px">
        <a-spin />
      </div>

      <div class="flex flex-column gap-3" v-else>
        <div class="flex gap-2 align-items-center">
          <h3 class="m-0" v-if="template">
            {{ template.name }}
          </h3>
          <a-tag class="m-0" :color="getAssociateDocumentStatus(document.status).color">
            {{ getAssociateDocumentStatus(document.status).title }}</a-tag
          >
        </div>

        <grouped-associate-info
          :first-name="document.associate.first_name"
          :last-name="document.associate.last_name"
          :associate-number="document.associate.associate_number"
          :role="document.associate.role.name"
          :unverified="!document.associate.is_verified"
        />

        <a-descriptions :column="1" size="small" class="col-fixed p-0">
          <a-descriptions-item v-if="template" label="Type">
            <a-tag class="m-0">{{ template.type.name }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="Origin" v-if="document.associate.payment_info?.legal_entity && document.origin">
            <a-tag
              class="m-0 ml-2"
              :style="{ maxWidth: '150px', textWrap: 'initial' }"
              :color="getAssociateDocumentOrigin(document.origin).color"
            >
              {{ document.external_project_name ?? getAssociateDocumentOrigin(document.origin).title }}</a-tag
            >
          </a-descriptions-item>
          <a-descriptions-item v-if="template" label="Description">
            {{ template.description ?? '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="Created at">
            {{ document.created_at ? formatDateString(document.created_at) : '-' }}
          </a-descriptions-item>

          <a-descriptions-item label="Required in Company">
            {{ document.required_in_project ? 'Yes' : 'No' }}
          </a-descriptions-item>

          <a-descriptions-item label="Author">
            <grouped-associate-info
              v-if="document.author"
              :first-name="document.author.first_name"
              :last-name="document.author.last_name"
              :photo-url="document.author.photo_url"
              :associate-number="document.author.associate_number"
              :role="document.associate.role.name"
            />
            <span v-else>-</span>
          </a-descriptions-item>

          <a-descriptions-item label="Approved By">
            <grouped-associate-info
              v-if="document.approved_by"
              :first-name="document.approved_by.first_name"
              :last-name="document.approved_by.last_name"
              :photo-url="document.approved_by.photo_url"
              :associate-number="document.approved_by.associate_number"
              :role="document.associate.role.name"
            />
            <span v-else>-</span>
          </a-descriptions-item>
        </a-descriptions>

        <a-form-model
          layout="horizontal"
          ref="form"
          class="flex gap-2 flex-column"
          :model="form"
          :rules="rules"
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 16 }"
        >
          <a-form-model-item :colon="false" label-align="left" label="Template" prop="template_id">
            <search-template :value="form.template_id" @update="onSearchUpdate" />
          </a-form-model-item>

          <template v-if="template">
            <a-form-model-item :colon="false" label-align="left" label="Name" prop="name">
              <a-input :disabled="true" :value="template.name" placeholder="My worker license" />
            </a-form-model-item>
            <a-form-model-item :colon="false" label-align="left" label="Details" prop="description">
              <a-textarea
                :disabled="true"
                :value="template.description"
                placeholder="My license to work on both sides"
              />
            </a-form-model-item>
            <a-form-model-item :colon="false" label-align="left" label="Type" prop="type">
              <a-input :disabled="true" :value="template.type.name" />
            </a-form-model-item>
            <a-form-model-item
              v-if="template.has_number"
              :colon="false"
              label-align="left"
              label="Document Number"
              prop="number"
            >
              <a-input v-model="form.number" placeholder="Document Number" />
            </a-form-model-item>
            <a-form-model-item
              v-if="template.can_expire"
              :colon="false"
              label-align="left"
              label="Expiration Date"
              prop="expiration_date"
            >
              <a-date-picker
                format="MM/DD/YYYY"
                placeholder="Expiration Date"
                v-model="form.expiration_date"
                valueFormat="YYYY-MM-DD"
              />
            </a-form-model-item>
          </template>

          <template v-else
            ><a-form-model-item :colon="false" label-align="left" label="Name" prop="name">
              <a-input v-model="form.name" placeholder="My worker license" />
            </a-form-model-item>
            <a-form-model-item :colon="false" label-align="left" label="Details" prop="description">
              <a-textarea v-model="form.description" placeholder="My license to work on both sides" />
            </a-form-model-item>
            <a-form-model-item :colon="false" label-align="left" label="Type" prop="type">
              <custom-select
                :multiple="false"
                :value.sync="form.type"
                placeholder="Type"
                :options="types"
                name="name"
                optionKey="id"
              /> </a-form-model-item
          ></template>

          <a-form-model-item prop="files" :colon="false" label-align="left" label="Attachments">
            <document-attachments
              :with-title="false"
              :files="form.files"
              :delete-confirmation="{
                title: 'Delete Attachment?',
                content:
                  'Are you sure you want to delete the document attachment? This will permanently delete the attachment, and you won’t be able to restore it.'
              }"
              @save="saveAttachmentsIds"
            />
          </a-form-model-item>
        </a-form-model></div
    ></template>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>

        <template v-if="document">
          <template v-if="document.status === 'unverified'">
            <div class="flex gap-2">
              <a-button type="danger" @click="updateDocument('rejected')">Reject</a-button>
              <a-button type="primary" @click="updateDocument('accepted')">Accept</a-button>
            </div>
          </template>

          <div v-else-if="document.status !== 'deleted'" class="flex gap-2">
            <a-button type="primary" @click="updateDocument(document.status)">Save </a-button>
            <a-button type="primary" @click="updateDocument('accepted', true)">Renew</a-button>
          </div>
        </template>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import ExportButton from '@/components/export-button.vue'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { deepCopy } from '@/utils/deep-copy'
import { Button, DatePicker, Descriptions, FormModel, notification, Spin, Tag, Input } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import AttachedCreateSearch from './attached-create-search.vue'

const { mapActions, mapGetters } = createNamespacedHelpers('document')
const { mapActions: refBookActions } = createNamespacedHelpers('refbook')

const initialForm = {
  number: undefined,
  expiration_date: undefined,
  template_id: undefined,
  files: []
}

const initialRules = {
  name: [
    {
      required: true,
      message: 'Name can not be empty',
      trigger: 'change'
    },
    {
      pattern: /^[^/\\]*$/,
      message: 'Symbols / and \\ are not allowed',
      trigger: 'change'
    }
  ],
  template_id: [
    {
      required: true,
      message: 'Template can not be empty',
      trigger: 'change'
    }
  ],
  type: [
    {
      required: true,
      message: 'Type can not be empty',
      trigger: 'change'
    }
  ],
  number: [
    {
      required: true,
      message: 'Number can not be empty',
      trigger: 'change'
    }
  ],
  expiration_date: [
    {
      required: true,
      message: 'Expiration date can not be empty',
      trigger: 'change'
    }
  ],
  files: [
    {
      required: true,
      message: 'Files can not be empty',
      trigger: 'change',
      type: 'array'
    }
  ]
}

export default {
  components: {
    'a-spin': Spin,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tag': Tag,
    'a-button': Button,
    'a-date-picker': DatePicker,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input': Input,
    'a-textarea': Input.TextArea,
    'custom-select': CustomSelect,
    'export-button': ExportButton,
    'grouped-associate-info': GroupedAssociateInfo,
    'document-attachments': ShiftFormAttachments,
    'search-template': AttachedCreateSearch
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialDocument: {
      type: Object,
      default: undefined
    },
    associate_id: {
      type: Number,
      default: undefined
    },
    request_id: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      document: undefined,
      form: { ...initialForm },
      rules: {
        ...initialRules
      },

      types: [],

      loading: false,
      loadingSave: false,

      attachmentsIds: [],

      template: null,
      serverDateFormat: this.$store.state.applicationState.serverDateFormat
    }
  },
  created() {
    this.rules.files = [
      {
        validator: this.validateAttachments,
        trigger: 'change'
      }
    ]
  },
  mounted() {
    if (this.visible && !this.types.length) this.fetchTypes()
  },
  watch: {
    visible(newVal) {
      if (newVal && !this.types.length) {
        this.fetchTypes()
      }

      if (newVal && this.associate_id && this.request_id) {
        this.fetchDocument()
      } else if (newVal && this.initialDocument) {
        this.document = deepCopy(this.initialDocument)
        this.form = deepCopy(this.initialDocument)
        this.templateId = this.document.template_id

        if (!this.templateId) {
          this.form.type = this.document.type.id
          this.noDate = this.document.expiration_date === null
          this.noNumber = this.document.number === null
        }

        this.attachmentsIds = this.document.files.map(item => ({
          id: item.id
        }))
      }
    },
    template: {
      handler(newVal) {
        if (!newVal) {
          return
        }

        if (newVal.id !== this.document.template_id) {
          this.form.expiration_date = undefined
          this.form.number = undefined
        }
      }
    }
  },
  computed: {
    ...mapGetters(['getAssociateDocumentStatus', 'getAssociateDocumentOrigin']),

    exportPayload() {
      return { type: 'associate_document', object_ids: [this.document.id] }
    }
  },
  methods: {
    ...mapActions([
      'getAssociateDocumentByRequest',
      'patchAssociateDocument',
      'createAssociateDocument',
      'getAssociateDocument'
    ]),
    ...refBookActions(['getRefBook']),

    async fetchTypes() {
      try {
        this.types = await this.getRefBook({ name: 'AssociateDocumentType' })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    onSearchUpdate(template) {
      this.template = deepCopy(template)
      this.$set(this.form, 'template_id', this.template.id)
    },

    async handleAttachedView() {
      await this.fetchParent()
    },

    saveAttachmentsIds(ids, flag) {
      this.attachmentsIds = ids

      if (this.$refs.form) {
        if (this.attachmentsIds.length > 0) {
          this.$refs.form.clearValidate(['files'])
        } else {
          this.$refs.form.validateField('files')
        }
      }

      if (flag !== 'remove') {
        return
      }

      if (!Array.isArray(this.form.files)) {
        this.form.files = []
        return
      }

      const attachmentIds = new Set(ids.map(item => item?.id).filter(id => typeof id === 'number'))

      // Keep form.files aligned on delete so invalid form rollback
      // does not restore already deleted files.
      this.form.files = this.form.files.filter(file => attachmentIds.has(file.id))
    },

    validateAttachments(rule, value, callback) {
      if (this.attachmentsIds.length > 0) {
        callback()
        return
      }

      callback(new Error('Files can not be empty'))
    },

    closeModal() {
      this.document = undefined
      this.template = null
      this.form = deepCopy(initialForm)
      this.templateId = null
      this.noDate = false
      this.noNumber = false
      this.$emit('update:visible', false)
    },

    formatDateString(dateString) {
      return moment(dateString).utc().format('MM/DD/YYYY')
    },

    async fetchDocument() {
      const payload = {
        associate_id: this.associate_id,
        request_id: this.request_id
      }

      this.loading = true

      try {
        const data = await this.getAssociateDocumentByRequest(payload)

        this.document = data
        this.form = data
        this.templateId = this.document.template_id
        this.attachmentsIds = this.document.files.map(item => ({
          id: item.id
        }))
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        this.closeModal()
      } finally {
        this.loading = false
      }
    },

    async fetchParent() {
      const payload = {
        associate_id: this.document.associate.id,
        document_id: this.document.parent_id
      }

      this.loading = true

      try {
        const data = await this.getAssociateDocument(payload)

        this.document = data
        this.form = data
        this.templateId = this.document.template_id
        this.attachmentsIds = this.document.files.map(item => ({
          id: item.id
        }))
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        this.closeModal()
      } finally {
        this.loading = false
      }
    },

    async updateDocument(status, isRenew) {
      const tempFiles = deepCopy(this.form.files)
      this.form.files = this.attachmentsIds

      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.form.files = tempFiles
          return
        }

        if (!this.template?.type?.id) {
          this.form.files = tempFiles
          this.showNotification('error', 'Error', 'Template is required')
          return
        }

        const payload = {
          document: {
            ...this.form,
            expiration_date: this.template.can_expire
              ? moment.utc(this.form.expiration_date).format(this.serverDateFormat)
              : undefined,
            number: this.template.has_number ? this.form.number : undefined,
            created_at: moment.utc().format(this.serverDateFormat),
            name: this.template.name,
            description: this.template.description,
            type: this.template.type.id,
            parent_id: undefined,
            status
          },
          associate_id: this.form.associate.id
        }

        if (isRenew) {
          payload.document.parent_id = this.form.id
        }

        this.loading = true

        try {
          const data = isRenew
            ? await this.createAssociateDocument(payload)
            : await this.patchAssociateDocument(payload)

          this.showNotification(
            'success',
            'Success',
            isRenew ? 'Document has been renewed successfully' : 'Document has been updated successfully'
          )

          if (isRenew) {
            this.$emit('renew', data)
          } else {
            this.$emit('update', data)
          }
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        } finally {
          this.closeModal()
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.ant-descriptions {
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
}

.document-actions {
  position: absolute;
  right: -250px;
  background-color: var(--bg-component);
  width: 230px;
  height: fit-content;
  top: 0;
  border-radius: 12px;
  padding: 16px 20px;
}

.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-calendar-picker {
  width: 100%;
}

.ant-checkbox-wrapper + .ant-checkbox-wrapper {
  margin: 0;
}
</style>
